import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC, useContext, useState } from 'react';

import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import { useLogInForm } from '@/components/ui/features/log-in/hooks/useLogInForm';
import { GoogleSocialSignInButton } from '@/components/ui/shared/social/SocialSignInButton/SocialSignInButton';

import { OverlayType } from '../../../../../enums';

export const ProtectedPageLogInSection: FC = () => {
  const { register, errors, onSubmit } = useLogInForm();
  const [showPassword, setShowPassword] = useState(false);
  const context = useContext(FadeInModalContext);

  return (
    <Container px={{ base: 0, sm: 4 }} pt={{ base: 4, md: 12 }} pb={{ base: 0, sm: 6, md: 12 }}>
      <Flex
        as="form"
        onSubmit={onSubmit}
        flexDirection="column"
        gap={4}
        w="full"
        maxW="539px"
        mx="auto"
        px={12}
        py={10}
        bg="white"
        borderWidth="1px"
        borderColor="gray.100"
        borderRadius={{ base: 0, sm: '24px' }}
        boxShadow="0px 8px 32px 0px rgba(0, 0, 0, 0.08)"
      >
        <Flex flexDirection="column" gap={2}>
          <Text fontSize="xs" fontWeight="semibold" lineHeight="18px" color="violet.500">
            Welcome back
          </Text>
          <Heading fontSize="2xl" lineHeight="32px">
            Sign in to access this page
          </Heading>
        </Flex>
        <Text lineHeight="22px">
          This page is available to logged-in users only. Please sign in to view and access its content.
        </Text>
        <Box>
          <FormControl mb={8} variant="floating" isInvalid={Boolean(errors.email)}>
            <Input
              placeholder=" "
              {...register('email', {
                required: 'Email address is required',
              })}
              variant="flushed"
              errorBorderColor="none"
              autoComplete="email"
              _invalid={{ boxShadow: 'none' }}
              _autofill={{
                transition: 'background-color 5000s ease-in-out 0s',
              }}
            />
            <FormLabel>Email address</FormLabel>
            <FormErrorMessage>{errors.email?.message as string}</FormErrorMessage>
          </FormControl>
          <FormControl mb={8} variant="floating" isInvalid={Boolean(errors.password)}>
            <InputGroup size="md">
              <Input
                placeholder=" "
                {...register('password', {
                  required: 'Password is required',
                })}
                type={showPassword ? 'text' : 'password'}
                variant="flushed"
                errorBorderColor="none"
                autoComplete="current-password"
                _invalid={{ boxShadow: 'none' }}
                _autofill={{
                  transition: 'background-color 5000s ease-in-out 0s',
                }}
              />
              <FormLabel>Password</FormLabel>
              <InputRightElement minW="max-content">
                <Button variant="unstyled" onClick={() => setShowPassword((show) => !show)}>
                  {showPassword ? 'hide' : 'show'}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password?.message as string}</FormErrorMessage>
          </FormControl>
          <Checkbox size="lg" colorScheme="violet" defaultChecked {...register('remember_me')}>
            Remember me
          </Checkbox>
        </Box>
        <Button type="submit" w="full" h="56px">
          Sign in
        </Button>
        <Button
          variant="unstyled"
          alignSelf="center"
          fontFamily="body"
          fontWeight="normal"
          onClick={() => context.showModalType.set(OverlayType.RESET_PASSWORD)}
        >
          Forgot password?
        </Button>
        <GoogleSocialSignInButton actionType="login">Sign in with Google</GoogleSocialSignInButton>
        <Flex gap={2} justifyContent="center" lineHeight="24px">
          <Text>Need help?</Text>
          <Link as={NextLink} href="/contact-us/support" fontWeight="semibold">
            Contact us.
          </Link>
        </Flex>
      </Flex>
    </Container>
  );
};
